/*
 * Copyright (c) CIRROM 2021.
 */

import React from "react";
import { GenericGridTabs } from "./GenericGridTabs";

/**
 * SuggestedChangesGridTabs component
 *
 *
 * This component displays tabs for the suggested changes grid generated by file upload
 *
 * @param {Object} props - Component props.
 *
 */
export function SuggestedChangesGridTabs(
  props: Readonly<{
    allPending: number;
    fillInBlank: number;
    correction: number;
    invalidData: number;
    allActioned: number;
    onSelectionChanged: (filterModel: any) => void;
  }>
) {
  const {
    allPending,
    fillInBlank,
    correction,
    invalidData,
    allActioned,
    onSelectionChanged,
  } = props;

  /**
   * Generates ag grid filter condition for each tab component
   *
   * @param filterType
   */
  const filterSuggestedChangesGrid = (filterType) => {
    switch (filterType) {
      case "blanks": {
        return {
          change_type: {
            filterType: "text",
            type: "equals",
            filter: "FILL_IN_BLANK",
            newRowsAction: "keep",
          },
        };
      }
      case "corrections": {
        return {
          change_type: {
            conditions: [
              {
                type: "equals",
                filter: "CORRECTION",
                newRowsAction: "keep",
              },
              {
                type: "equals",
                filter: "STANDARDIZATION",
                newRowsAction: "keep",
              },
            ],
            filterType: "text",
            operator: "OR",
          },
        };
      }
      case "manuals": {
        return {
          change_type: {
            conditions: [
              {
                type: "equals",
                filter: "INVALID_DATA",
                newRowsAction: "keep",
              },
              {
                type: "equals",
                filter: "INFORMATION",
                newRowsAction: "keep",
              },
            ],
            filterType: "text",
            operator: "OR",
          },
        };
      }
      case "all pending": {
        return {
          change_status: {
            conditions: [
              {
                filterType: "text",
                type: "notEqual",
                filter: "VALIDATED",
                newRowsAction: "keep",
              },
              {
                filterType: "text",
                type: "notEqual",
                filter: "HUMAN_CONFIRMED",
                newRowsAction: "keep",
              },
            ],
            operator: "AND",
          },
        };
      }
      case "actioned": {
        return {
          change_status: {
            conditions: [
              {
                filterType: "text",
                type: "equals",
                filter: "VALIDATED",
              },
              {
                filterType: "text",
                type: "equals",
                filter: "HUMAN_CONFIRMED",
              },
            ],
            filterType: "text",
            operator: "OR",
          },
          operator: "OR",
        };
      }
    }
  };

  /**
   * Build the grid tabs by label and associated filters
   */
  const gridTabs = [
    {
      name: "all pending",
      label: "Pending",
      filters: filterSuggestedChangesGrid("all pending"),
      totalItems: allPending,
    },
    {
      name: "blanks",
      label: "Empty Fields",
      filters: filterSuggestedChangesGrid("blanks"),
      totalItems: fillInBlank,
    },
    {
      name: "corrections",
      label: "Proposed Corrections",
      filters: filterSuggestedChangesGrid("corrections"),
      totalItems: correction,
    },
    {
      name: "manuals",
      label: "Manual Actions",
      filters: filterSuggestedChangesGrid("manuals"),
      totalItems: invalidData,
    },
    {
      name: "actioned",
      label: "Actioned",
      filters: filterSuggestedChangesGrid("actioned"),
      totalItems: allActioned,
    },
  ];

  return (
    <GenericGridTabs
      onSelectionChanged={onSelectionChanged}
      gridTabs={gridTabs}
      defaultSelectedTab={"all pending"}
    />
  );
}
